import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { login } from "@/lib/auth";
import GMark from "@/components/GMark";
import GoogleGlyph from "@/components/GoogleGlyph";
import PhotoUpload from "@/components/PhotoUpload";
import PasswordStrength from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";
import Seo from "@/components/Seo";

// Titles/paths for each SellerAuth mode. Auth pages are always noindex — they
// hold no unique public content and would otherwise flag as duplicate/low-word.
const AUTH_SEO = {
  login:  { title: "Log in",  path: "/seller/login",  desc: "Log in to your Gate to manage products, orders and payouts." },
  signup: { title: "Open your Gate", path: "/seller/signup", desc: "Set up your Gate in under three minutes — name it, add your first plant, and open for orders." },
  forgot: { title: "Reset your password", path: "/seller/forgot", desc: "Send yourself a one-time link to reset the password on your Gate." },
  reset:  { title: "Set a new password", path: "/seller/reset", desc: "Set a new password on your Gate." },
};

// Emergent-managed Google auth kicks off here.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The redirect must be derived from the browser's
// live origin so it survives preview URL / custom-domain / subdomain.
function startGoogleSignIn() {
  const redirect = window.location.origin + "/dashboard";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
}

function GoogleButton({ label = "Continue with Google", testid = "google-signin-btn" }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={startGoogleSignIn}
      className="w-full h-11 flex items-center justify-center gap-3 rounded-full border border-stone-300 bg-white hover:bg-stone-50 text-sm font-medium text-[#20251F] transition-colors"
    >
      <GoogleGlyph size={18} /> {label}
    </button>
  );
}

function AuthDivider({ label = "or" }) {
  return (
    <div className="flex items-center gap-3 my-5" aria-hidden="true">
      <div className="flex-1 h-px bg-stone-200" />
      <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-stone-500">
        {label}
      </span>
      <div className="flex-1 h-px bg-stone-200" />
    </div>
  );
}

function BrandHeader() {
  return (
    <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="auth-brand-header">
      <GMark size={32} className="rounded-lg" />
      <span className="font-serif-heading text-2xl">Gate<span className="brand-plus text-[1.05em]"> + </span>Gather</span>
    </Link>
  );
}

export default function SellerAuth({ mode }) {
  const nav = useNavigate();
  const seo = AUTH_SEO[mode] || AUTH_SEO.signup;
  const inner =
    mode === "login" ? <LoginForm nav={nav} />
    : mode === "forgot" ? <ForgotForm />
    : mode === "reset" ? <ResetForm nav={nav} />
    : <SignupWizard nav={nav} />;
  return (
    <>
      <Seo title={seo.title} description={seo.desc} path={seo.path} noindex />
      {inner}
    </>
  );
}

function LoginForm({ nav }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [remember, setRemember] = useState(() => {
    try { return localStorage.getItem("gg-remember") !== "0"; } catch { return true; }
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { ...form, remember });
      try { localStorage.setItem("gg-remember", remember ? "1" : "0"); } catch {}
      login(data.token, data.seller);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F5F0E5]">
      <div className="w-full max-w-md">
        <BrandHeader />
        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight">Welcome back</h1>
          <p className="text-sm text-stone-600 mt-1">Log in to manage your Gate.</p>
          <div className="mt-6">
            <GoogleButton label="Continue with Google" testid="login-google-btn" />
          </div>
          <AuthDivider label="or sign in with email" />
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Email</Label><Input data-testid="auth-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <Link to="/seller/forgot" data-testid="forgot-password-link" className="text-xs text-[#3F674E] hover:text-[#20251F] underline underline-offset-2">Forgot password?</Link>
              </div>
              <PasswordInput data-testid="auth-password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
            </div>
            <label className="flex items-start gap-3 pt-1 cursor-pointer select-none" data-testid="remember-me-row">
              <Checkbox
                data-testid="remember-me-checkbox"
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                className="mt-0.5 border-[#20251F] data-[state=checked]:bg-[#20251F] data-[state=checked]:border-[#20251F]"
              />
              <span className="text-sm text-[#20251F] leading-snug">
                Keep me signed in on this device for 30 days
                <span className="block text-xs text-[#6B6E67] mt-0.5">Uncheck if you are on a shared phone or computer.</span>
              </span>
            </label>
            <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-[#20251F] hover:bg-black rounded-full text-white">{busy ? "…" : "Log in"}</Button>
          </form>
          <p className="text-sm text-stone-600 mt-6 text-center">New here? <Link data-testid="auth-toggle-link" to="/seller/signup" className="text-[#3F674E] font-medium underline">Open your Gate</Link></p>
        </div>
      </div>
    </div>
  );
}

function ForgotForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F5F0E5]">
      <div className="w-full max-w-md">
        <BrandHeader />
        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight">Forgot your password?</h1>
          {!sent ? (
            <>
              <p className="text-sm text-stone-600 mt-1">Enter the email tied to your Gate and we'll send a reset link. The link lasts one hour.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div><Label>Email</Label><Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" placeholder="you@yourgate.co.nz" /></div>
                <Button data-testid="forgot-submit-btn" type="submit" disabled={busy || !email} className="w-full h-11 bg-[#20251F] hover:bg-black rounded-full text-white">{busy ? "Sending…" : "Send reset link"}</Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success" className="mt-2">
              <p className="font-editorial text-lg text-[#20251F]">Check your inbox.</p>
              <p className="text-sm text-stone-600 mt-1">If an account exists for <span className="font-mono-label text-[#3F674E]">{email}</span>, a reset link is on its way. It lasts one hour and can only be used once.</p>
              <p className="text-xs text-stone-500 mt-4">Nothing in a few minutes? Check spam, or <button onClick={() => setSent(false)} className="underline text-[#3F674E]">try again</button>.</p>
            </div>
          )}
          <p className="text-sm text-stone-600 mt-6 text-center"><Link to="/seller/login" className="text-[#3F674E] font-medium underline">Back to log in</Link></p>
        </div>
      </div>
    </div>
  );
}

function ResetForm({ nav }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const mismatch = pw && pw2 && pw !== pw2;
  const tooShort = pw && pw.length < 6;
  const submit = async (e) => {
    e.preventDefault();
    if (mismatch || tooShort || !token) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      login(data.token, data.seller);
      toast.success("Password updated");
      nav("/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F5F0E5]">
      <div className="w-full max-w-md">
        <BrandHeader />
        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight">Set a new password</h1>
          {!token ? (
            <p className="text-sm text-red-600 mt-2" data-testid="reset-invalid">This reset link is missing its token. Request a fresh one from the <Link to="/seller/forgot" className="underline">forgot password page</Link>.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label>New password</Label><PasswordInput data-testid="reset-pw-input" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" /><PasswordStrength password={pw} /></div>
              <div><Label>Confirm new password</Label><PasswordInput data-testid="reset-pw2-input" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" /></div>
              {tooShort && <p className="text-xs text-red-600">At least 6 characters.</p>}
              {mismatch && <p className="text-xs text-red-600">Passwords don't match.</p>}
              <Button data-testid="reset-submit-btn" type="submit" disabled={busy || !pw || !pw2 || mismatch || tooShort} className="w-full h-11 bg-[#20251F] hover:bg-black rounded-full text-white">{busy ? "Saving…" : "Save new password"}</Button>
            </form>
          )}
          <p className="text-sm text-stone-600 mt-6 text-center"><Link to="/seller/login" className="text-[#3F674E] font-medium underline">Back to log in</Link></p>
        </div>
      </div>
    </div>
  );
}

function SignupWizard({ nav }) {
  // Google onboarding query params — set by AuthCallback when Emergent returns
  // an email that has no matching seller row yet. `google=1` means we hide the
  // password field and mark the signup as OAuth-only on the backend.
  const [qp] = useSearchParams();
  const googleMode = qp.get("google") === "1";
  const googleEmail = qp.get("email") || "";
  const googleName = qp.get("name") || "";

  // Plan preselect from the public pricing cards. `?plan=gate` and
  // `?plan=gateplus` deep-link into the wizard with that plan already
  // highlighted on Step 4. Any other value falls back to the default so a
  // typo can never route the seller to a plan that doesn't exist in the
  // backend PLANS dict.
  const rawPlan = (qp.get("plan") || "").toLowerCase();
  const preselectedPlan = (rawPlan === "gate" || rawPlan === "honesty_box") ? rawPlan : "gate";

  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    business_name: googleName,  // seed with the Google full name as a suggestion; user edits
    location: "",
    product: { photo_url: null, name: "", price: "", quantity: "" },
    email: googleEmail,
    password: "",
    plan_id: preselectedPlan,
  });
  const [voucher, setVoucher] = useState({ code: "", checking: false, valid: null, message: "" });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  // HARD REQUIREMENT (Brief #3 §8): No account, email or password is asked or created
  // until step 4. Steps 1–3 mutate ONLY local state — zero backend calls, zero auth.
  // The single POST /api/auth/signup happens inside `finish()` alongside the store
  // update and first product creation. Do not reorder this to email-first.
  const upd = (patch) => setData((d) => ({ ...d, ...patch }));
  const updProd = (patch) => setData((d) => ({ ...d, product: { ...d.product, ...patch } }));

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(1, s - 1));

  const onFile = (file) => {
    if (!file) return;
    setUploadedFile(file);
    updProd({ photo_url: URL.createObjectURL(file) });
  };

  const finish = async () => {
    setBusy(true);
    try {
      // 1. Create account — Google users skip password (backend hashes a
      // random secret so /forgot can enable password login later). We also
      // hand the picked plan to the signup call: for `honesty_box` the
      // backend initialises the 14-day trial atomically so the POST /products
      // in step 4 below isn't rejected by the paid-plan paused-guard.
      const signupBody = {
        email: data.email,
        business_name: data.business_name,
        accepted_policy: agreed,
        plan_id: data.plan_id || undefined,
      };
      if (googleMode) {
        signupBody.google = true;
      } else {
        signupBody.password = data.password;
      }
      const { data: signup } = await api.post("/auth/signup", signupBody);
      login(signup.token, signup.seller);
      // 2. Save location
      if (data.location) await api.put("/store", { location: data.location });
      // 3. Upload photo if provided
      let photo_url = null;
      if (uploadedFile) {
        const fd = new FormData(); fd.append("file", uploadedFile);
        const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_url = up.url;
      }
      // 4. Create first product
      if (data.product.name && data.product.price !== "" && data.product.quantity !== "") {
        await api.post("/products", {
          name: data.product.name,
          price: parseFloat(data.product.price),
          quantity: parseInt(data.product.quantity, 10),
          state: "available",
          photo_url,
        });
      }
      // 5. Paid plan → hand off to Stripe subscription checkout. Honesty
      // Box → activate the 14-day trial server-side, no Stripe. Community
      // and any errored lookup fall through to /dashboard so the seller
      // isn't stranded — they can subscribe from Settings later.
      if (data.plan_id === "honesty_box") {
        try {
          await api.post("/plan/honesty-box/activate");
          toast.success("Your Honesty Box is open — 14 days free");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't start your trial — try again from Settings.");
        }
        nav("/dashboard");
        return;
      }
      if (data.plan_id === "gate") {
        try {
          const { data: co } = await api.post("/plan/checkout", {
            plan_id: data.plan_id,
            origin_url: window.location.origin,
            voucher_code: voucher.valid ? voucher.code.trim().toUpperCase() : undefined,
          });
          window.location.href = co.checkout_url;
          return;
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't start subscription — you can pick a plan from Settings.");
        }
      }
      toast.success("Your Gate is open");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create your Gate");
    } finally { setBusy(false); }
  };

  const checkVoucher = async () => {
    const code = voucher.code.trim();
    if (!code) return;
    setVoucher((v) => ({ ...v, checking: true, valid: null, message: "" }));
    // Voucher check requires an authenticated seller. We haven't signed up
    // yet at this step, so we cache the code and validate it at checkout on
    // the server (which also handles single-use enforcement). Show optimistic
    // feedback here so the seller knows the code is captured.
    setVoucher((v) => ({ ...v, checking: false, valid: true, message: "Voucher will be applied at checkout." }));
  };

  return (
    <div className="min-h-screen bg-[#F5F0E5] px-4 py-8">
      <Link to="/" className="flex items-center justify-center gap-2 mb-6" data-testid="signup-brand">
        <GMark size={32} className="rounded-lg" />
        <span className="font-serif-heading text-2xl">Gate<span className="brand-plus text-[1.05em]"> + </span>Gather</span>
      </Link>

      <div className="max-w-md mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`h-1.5 w-10 rounded-full ${step >= n ? "bg-[#20251F]" : "bg-stone-200"}`} />
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 sm:p-8 fade-in">
          {step === 1 && (
            <div>
              {!googleMode && (
                <>
                  <GoogleButton label="Sign up with Google" testid="signup-google-btn" />
                  <AuthDivider label="or set up by email" />
                </>
              )}
              <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80">Step 1 of 4</p>
              <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] mt-1">What should we call your Gate?</h1>
              <Label className="mt-6 block">Your business or grower name</Label>
              <Input data-testid="wiz-name" autoFocus value={data.business_name} onChange={(e) => upd({ business_name: e.target.value })} placeholder="Petal Power" className="mt-1" />
              <p className="text-xs text-stone-500 mt-2">Your customers will see this at the top of your Gate.</p>
              <Button data-testid="wiz-next-1" disabled={!data.business_name.trim()} onClick={next} className="w-full h-11 mt-6 rounded-full bg-[#20251F] hover:bg-black text-white">Next <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80">Step 2 of 4</p>
              <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] mt-1">Where are you based?</h1>
              <Label className="mt-6 block">General location</Label>
              <Input data-testid="wiz-location" autoFocus value={data.location} onChange={(e) => upd({ location: e.target.value })} placeholder="Te Puna, Bay of Plenty" className="mt-1" />
              <p className="text-xs text-stone-500 mt-2">A town or region is enough — exact pickup details can wait.</p>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={back} className="h-11 rounded-full"><ArrowLeft className="w-4 h-4" /></Button>
                <Button data-testid="wiz-next-2" disabled={!data.location.trim()} onClick={next} className="flex-1 h-11 rounded-full bg-[#20251F] hover:bg-black text-white">Next <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80">Step 3 of 4</p>
              <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] mt-1">Add your first plant</h1>
              <p className="text-xs text-stone-500 mt-1">Photo, name, price, quantity. That's it.</p>
              <div className="mt-4">
                <PhotoUpload
                  testid="wiz-photo"
                  initialPreview={data.product.photo_url}
                  onFile={onFile}
                />
              </div>
              <div className="mt-4"><Label>Plant name</Label><Input data-testid="wiz-plant-name" value={data.product.name} onChange={(e) => updProd({ name: e.target.value })} placeholder="Kōwhai" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label>Price ($)</Label><Input data-testid="wiz-plant-price" type="number" step="0.01" value={data.product.price} onChange={(e) => updProd({ price: e.target.value })} className="mt-1" /></div>
                <div><Label>Quantity</Label>
                  <div className="mt-1 flex items-center gap-2 bg-stone-100 rounded-full p-1 h-10 justify-between">
                    <button type="button" data-testid="wiz-qty-minus" onClick={() => updProd({ quantity: String(Math.max(0, parseInt(data.product.quantity || 0, 10) - 1)) })} className="w-8 h-8 bg-white rounded-full font-bold">−</button>
                    <span className="font-mono-label text-sm" data-testid="wiz-qty-val">{data.product.quantity || 0}</span>
                    <button type="button" data-testid="wiz-qty-plus" onClick={() => updProd({ quantity: String(parseInt(data.product.quantity || 0, 10) + 1) })} className="w-8 h-8 bg-white rounded-full font-bold">+</button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={back} className="h-11 rounded-full"><ArrowLeft className="w-4 h-4" /></Button>
                <Button data-testid="wiz-next-3" disabled={!data.product.name || !data.product.price || !data.product.quantity} onClick={next} className="flex-1 h-11 rounded-full bg-[#20251F] hover:bg-black text-white">Preview my Gate <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80">Nearly there</p>
              <h1 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] mt-1"><span className="italic">{data.business_name}</span> is ready to open.</h1>
              <div className="mt-4 rounded-2xl border border-stone-200 overflow-hidden bg-stone-50">
                {data.product.photo_url && <img src={data.product.photo_url} alt="" className="w-full h-32 object-cover" loading="lazy" decoding="async" />}
                <div className="p-4">
                  <p className="font-serif-heading text-lg">{data.business_name}</p>
                  <p className="text-xs text-stone-600">{data.location}</p>
                  <div className="mt-3 flex items-center justify-between border-t border-stone-200 pt-3">
                    <p className="font-editorial text-base">{data.product.name}</p>
                    <p className="font-mono-label text-sm">${data.product.price} · <span className="text-[#3F674E]">{data.product.quantity} available</span></p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-stone-700 mt-5">
                {googleMode ? "We'll use your Google email for orders." : "Where should we send your orders?"}
              </p>
              <div className="mt-3 space-y-3">
                <div>
                  <Label>Email</Label>
                  <Input
                    data-testid="wiz-email"
                    type="email"
                    value={data.email}
                    onChange={(e) => upd({ email: e.target.value })}
                    className="mt-1"
                    disabled={googleMode}
                    readOnly={googleMode}
                  />
                  {googleMode && (
                    <p className="text-xs text-[#3F674E] mt-1 flex items-center gap-1.5">
                      <GoogleGlyph size={12} /> Verified via Google
                    </p>
                  )}
                </div>
                {!googleMode && (
                  <div>
                    <Label>Choose a password</Label>
                    <PasswordInput data-testid="wiz-password" minLength={6} value={data.password} onChange={(e) => upd({ password: e.target.value })} className="mt-1" />
                    <PasswordStrength password={data.password} />
                  </div>
                )}
              </div>

              {/* Plan picker — sits under the credentials so the seller sees the
                  bill on the same screen as their email. Community is not on
                  the picker; schools/clubs are handled by a separate review flow. */}
              <div className="mt-6" data-testid="wiz-plan-picker">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-[#3F674E]/80">Choose your plan</p>
                <div className="mt-3 grid gap-2.5">
                  {[
                    { id: "honesty_box", label: "Honesty Box", price: "Free 14 days", cap: "1 active product", copy: "Try Gate + Gather. No card required." },
                    { id: "gate", label: "Gate", price: "$20/mo", cap: "25 new products / month", copy: "For independent growers selling regularly." },
                    { id: "gateplus", label: "Gate+", price: "Coming soon", cap: "", copy: "For bigger ranges and busier Gates.", disabled: true },
                  ].map((p) => {
                    const active = data.plan_id === p.id;
                    const disabled = !!p.disabled;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={`wiz-plan-${p.id}`}
                        onClick={() => !disabled && upd({ plan_id: p.id })}
                        disabled={disabled}
                        className={`text-left rounded-2xl border p-3.5 transition-colors ${disabled ? "border-stone-200 bg-stone-50 opacity-60 cursor-not-allowed" : active ? "border-[#20251F] bg-[#20251F]/5 ring-1 ring-[#20251F]/10" : "border-stone-200 bg-white hover:border-stone-300"}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-serif-heading text-lg text-[#20251F]">{p.label}</span>
                          <span className="font-mono-label text-sm text-[#20251F]">{p.price}</span>
                        </div>
                        <p className="text-xs text-stone-600 mt-1">{p.copy}</p>
                        {p.cap && <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/80 mt-2">{p.cap}</p>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3">
                  <details className="text-xs" data-testid="wiz-voucher-details">
                    <summary className="text-[#3F674E] cursor-pointer select-none inline-flex items-center gap-1">Have a founder voucher?</summary>
                    <div className="mt-2 flex gap-2">
                      <Input
                        data-testid="wiz-voucher-input"
                        value={voucher.code}
                        onChange={(e) => setVoucher((v) => ({ ...v, code: e.target.value, valid: null, message: "" }))}
                        placeholder="FOUNDER3"
                        className="flex-1 h-9 text-sm uppercase tracking-wider"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={checkVoucher}
                        disabled={voucher.checking || !voucher.code.trim()}
                        data-testid="wiz-voucher-apply"
                        className="h-9 rounded-full"
                      >
                        Apply
                      </Button>
                    </div>
                    {voucher.message && (
                      <p className={`text-[11px] mt-1.5 ${voucher.valid ? "text-[#3F674E]" : "text-red-600"}`} data-testid="wiz-voucher-msg">
                        {voucher.message}
                      </p>
                    )}
                  </details>
                  <p className="text-[10px] text-stone-500 mt-2">
                    For schools, clubs and community fundraisers,{" "}
                    <Link to="/faq" className="underline text-[#3F674E]">contact us</Link>{" "}
                    — no card required.
                  </p>
                </div>
              </div>
              <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="signup-policy-row">
                <Checkbox
                  data-testid="signup-policy-checkbox"
                  checked={agreed}
                  onCheckedChange={(v) => setAgreed(!!v)}
                  className="mt-0.5 border-[#20251F] data-[state=checked]:bg-[#20251F] data-[state=checked]:border-[#20251F]"
                />
                <span className="text-sm text-[#20251F] leading-snug">
                  <span className="font-editorial italic">Sell good things, fairly.</span>
                  <span className="block text-xs text-[#6B6E67] mt-1 leading-relaxed">
                    By opening your Gate, you confirm that the products you list are legal to sell, accurately described and permitted under the{" "}
                    <Link to="/policy/seller-policy" target="_blank" rel="noreferrer" data-testid="signup-policy-link" className="underline text-[#3F674E] hover:text-[#20251F]">Seller Policy</Link>{" "}
                    and{" "}
                    <Link to="/policy/terms" target="_blank" rel="noreferrer" data-testid="signup-terms-link" className="underline text-[#3F674E] hover:text-[#20251F]">Terms</Link>.
                  </span>
                </span>
              </label>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={back} className="h-11 rounded-full"><ArrowLeft className="w-4 h-4" /></Button>
                <Button
                  data-testid="wiz-finish"
                  disabled={
                    busy ||
                    !data.email ||
                    (!googleMode && (data.password || "").length < 6) ||
                    !agreed
                  }
                  onClick={finish}
                  className="flex-1 h-11 rounded-full bg-[#20251F] hover:bg-black text-white"
                >
                  {busy ? "Opening…" : <>{data.plan_id === "gate" ? "Continue to payment" : data.plan_id === "honesty_box" ? "Start my 14-day Honesty Box" : "Create my Gate"} <Check className="w-4 h-4 ml-1.5" /></>}
                </Button>
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-stone-500 text-center mt-4">Already have a Gate? <Link to="/seller/login" className="underline text-[#3F674E]">Log in</Link></p>
      </div>
    </div>
  );
}
